import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Brain,
  ArrowRight,
  ArrowLeft,
  Check,
  FileText,
  Cpu,
  Database,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  SkipForward,
  Info
} from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';
import api from '../../services/api';

const trainingSteps = [
  {
    id: 'documents',
    name: 'Processing Documents',
    description: 'Reading and extracting information from your uploaded files',
    icon: FileText,
    duration: 3000,
  },
  {
    id: 'embedding',
    name: 'Creating Knowledge Base',
    description: 'Converting your content into AI-readable format',
    icon: Database,
    duration: 4000,
  },
  {
    id: 'model',
    name: 'Configuring AI Model',
    description: 'Setting up your chosen AI brain with your preferences',
    icon: Cpu,
    duration: 2500,
  },
  {
    id: 'training',
    name: 'Training Your Assistant',
    description: 'Teaching your AI about your business and how to respond',
    icon: Brain,
    duration: 5000,
  },
  {
    id: 'optimization',
    name: 'Optimizing Responses',
    description: 'Fine-tuning for faster and more accurate answers',
    icon: Sparkles,
    duration: 2000,
  },
];

export default function StepTraining() {
  const { goNext, goPrev } = useOutletContext();
  const { currentAgent, trainAgent, trainingStatus, error, clearError, knowledgeFiles } = useAgent();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [hasDocuments, setHasDocuments] = useState(true);
  const [isCheckingDocuments, setIsCheckingDocuments] = useState(true);

  // Check if there are knowledge files
  useEffect(() => {
    const checkDocuments = async () => {
      setIsCheckingDocuments(true);
      try {
        // Check from context first, then fetch from API
        if (knowledgeFiles && knowledgeFiles.length > 0) {
          setHasDocuments(true);
        } else if (currentAgent?.id) {
          const result = await api.getKnowledgeFiles(currentAgent.id);
          setHasDocuments((result.files || []).length > 0);
        } else {
          setHasDocuments(false);
        }
      } catch (err) {
        console.error('Failed to check documents:', err);
        setHasDocuments(false);
      } finally {
        setIsCheckingDocuments(false);
      }
    };
    checkDocuments();
  }, [currentAgent?.id, knowledgeFiles]);

  // Fetch model info from backend
  useEffect(() => {
    const fetchModelInfo = async () => {
      try {
        const result = await api.getAvailableModels();
        const models = result.models || [];
        const modelId = currentAgent?.model || 'openai/gpt-4o-mini';
        const model = models.find(m => m.id === modelId);
        setSelectedModel(model);
      } catch (err) {
        console.error('Failed to fetch model info:', err);
      }
    };
    fetchModelInfo();
  }, [currentAgent?.model]);

  // Update UI based on backend training status
  useEffect(() => {
    if (trainingStatus?.status === 'completed') {
      setCompletedSteps(trainingSteps.map(s => s.id));
      setIsComplete(true);
    } else if (trainingStatus?.status === 'error') {
      setHasError(true);
    }
  }, [trainingStatus]);

  // Show backend errors
  useEffect(() => {
    if (error && isStarted) {
      setHasError(true);
    }
  }, [error, isStarted]);

  const startTraining = useCallback(async () => {
    setIsStarted(true);
    setCurrentStepIndex(0);
    setStepProgress(0);
    setCompletedSteps([]);
    setIsComplete(false);
    setHasError(false);

    try {
      await trainAgent();
    } catch (err) {
      console.error('Training failed:', err);
      setHasError(true);
    }
  }, [trainAgent]);

  useEffect(() => {
    if (!isStarted || isComplete || hasError) return;

    const currentStep = trainingSteps[currentStepIndex];
    if (!currentStep) {
      setIsComplete(true);
      return;
    }

    // Progress animation
    const progressInterval = setInterval(() => {
      setStepProgress((prev) => {
        if (prev >= 100) return 100;
        // Randomize progress speed for realism
        const increment = Math.random() * 3 + 1;
        return Math.min(prev + increment, 100);
      });
    }, currentStep.duration / 100);

    // Step completion
    const stepTimeout = setTimeout(() => {
      setCompletedSteps((prev) => [...prev, currentStep.id]);
      setStepProgress(0);

      if (currentStepIndex < trainingSteps.length - 1) {
        setCurrentStepIndex((prev) => prev + 1);
      } else {
        setIsComplete(true);
      }
    }, currentStep.duration);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(stepTimeout);
    };
  }, [currentStepIndex, isStarted, isComplete, hasError]);

  const totalProgress = isComplete
    ? 100
    : Math.round(
        ((completedSteps.length * 100) + stepProgress) / trainingSteps.length
      );

  const getStepStatus = (stepId) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (trainingSteps[currentStepIndex]?.id === stepId && isStarted && !isComplete) return 'in-progress';
    return 'pending';
  };

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Training Your AI</h1>
            <p className="text-text-secondary">We're setting up your assistant with everything it needs</p>
          </div>
        </div>
      </div>

      {/* Loading state while checking documents */}
      {isCheckingDocuments && (
        <div className="card p-8 text-center mb-6">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Checking your knowledge base...</p>
        </div>
      )}

      {/* No Documents - Skip Training Option */}
      {!isCheckingDocuments && !hasDocuments && !isStarted && (
        <div className="card p-8 text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Info className="w-10 h-10 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Knowledge Base Documents</h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            You haven't uploaded any documents to train your AI. Your assistant will use your business description to respond to customers.
          </p>
          <p className="text-sm text-text-muted mb-6">
            You can add documents later from the agent dashboard to improve responses.
          </p>
          <Button onClick={goNext} icon={SkipForward}>
            Skip Training & Continue
          </Button>
        </div>
      )}

      {/* Model Info - Only show when has documents */}
      {!isCheckingDocuments && hasDocuments && selectedModel && (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl flex items-center gap-3">
          <Cpu className="w-5 h-5 text-primary" />
          <div>
            <span className="text-sm text-text-muted">AI Model: </span>
            <span className="font-medium text-text-primary">{selectedModel.name}</span>
            <span className="text-sm text-text-muted"> by {selectedModel.provider}</span>
          </div>
        </div>
      )}

      {/* Start Training Button (before started) - Only show when has documents */}
      {!isCheckingDocuments && hasDocuments && !isStarted && (
        <div className="card p-8 text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Brain className="w-10 h-10 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">Ready to Train Your AI</h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            We'll process your documents, configure your AI model, and prepare your assistant
            to handle customer conversations.
          </p>
          <Button onClick={startTraining} icon={Sparkles}>
            Start Training
          </Button>
        </div>
      )}

      {/* Training Progress (after started) */}
      {isStarted && (
        <>
          {/* Overall Progress Bar */}
          <div className="card p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-text-primary">
                {isComplete ? 'Training Complete!' : 'Training Progress'}
              </span>
              <span className="text-sm font-semibold text-primary">{totalProgress}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  isComplete
                    ? 'bg-gradient-to-r from-green-400 to-green-500'
                    : 'bg-gradient-to-r from-primary to-accent'
                }`}
                style={{ width: `${totalProgress}%` }}
              />
            </div>
            {!isComplete && (
              <p className="text-xs text-text-muted mt-2">
                This usually takes about 30 seconds. Please don't close this page.
              </p>
            )}
          </div>

          {/* Training Steps */}
          <div className="card divide-y divide-border">
            {trainingSteps.map((step, index) => {
              const status = getStepStatus(step.id);
              const Icon = step.icon;

              return (
                <div key={step.id} className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Status Icon */}
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                      ${status === 'completed'
                        ? 'bg-green-100'
                        : status === 'in-progress'
                        ? 'bg-primary/10'
                        : 'bg-gray-100'
                      }
                    `}>
                      {status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : status === 'in-progress' ? (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      ) : (
                        <Icon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-medium ${
                          status === 'completed'
                            ? 'text-green-600'
                            : status === 'in-progress'
                            ? 'text-primary'
                            : 'text-text-muted'
                        }`}>
                          {step.name}
                        </h4>
                        {status === 'completed' && (
                          <span className="text-xs text-green-500">Done</span>
                        )}
                      </div>
                      <p className={`text-sm ${
                        status === 'pending' ? 'text-text-muted' : 'text-text-secondary'
                      }`}>
                        {step.description}
                      </p>

                      {/* Step Progress Bar */}
                      {status === 'in-progress' && (
                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-100"
                            style={{ width: `${stepProgress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Completion Message */}
          {isComplete && (
            <div className="mt-6 p-6 bg-green-50 rounded-xl border border-green-100 text-center animate-slide-up">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">
                Your AI is Ready!
              </h3>
              <p className="text-green-700 mb-4">
                Training completed successfully. Your assistant is now ready to help your customers.
              </p>
              <div className="flex items-center justify-center gap-4 text-sm text-green-600">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  <span>Documents processed</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  <span>Model configured</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  <span>Ready to chat</span>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {hasError && (
            <div className="mt-6 p-6 bg-red-50 rounded-xl border border-red-100 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Training Failed
              </h3>
              <p className="text-red-700 mb-4">
                {error || trainingStatus?.error || 'Something went wrong during training. Please try again.'}
              </p>
              <Button variant="secondary" onClick={() => { clearError(); startTraining(); }} icon={RefreshCw}>
                Retry Training
              </Button>
            </div>
          )}
        </>
      )}

      {/* Tips - Only show when has documents */}
      {!isCheckingDocuments && hasDocuments && (
        <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
          <h4 className="text-sm font-medium text-amber-800 mb-2">What happens during training?</h4>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>1. We read all your uploaded documents and extract key information</li>
            <li>2. Your content is converted into a special format the AI can understand</li>
            <li>3. We configure your chosen AI model with your business personality</li>
            <li>4. The AI learns how to respond based on your knowledge base</li>
            <li>5. Finally, we optimize everything for fast, accurate responses</li>
          </ul>
        </div>
      )}

      {/* Navigation - Hide when no documents (skip button is shown above instead) */}
      {!isCheckingDocuments && (hasDocuments || isStarted) && (
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={goPrev}
            disabled={isStarted && !isComplete}
          >
            Back
          </Button>
          <Button
            icon={ArrowRight}
            iconPosition="right"
            onClick={goNext}
            disabled={!isComplete}
          >
            Continue
          </Button>
        </div>
      )}

      {/* Back button only when no documents and not started */}
      {!isCheckingDocuments && !hasDocuments && !isStarted && (
        <div className="flex items-center mt-8">
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={goPrev}
          >
            Back
          </Button>
        </div>
      )}
    </div>
  );
}
